import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { ChevronDown } from 'lucide-react';
import { ApiError, apiFetch } from '../api/client.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { Card } from '../components/Card.tsx';
import { ReportsHeader, usePeriod } from '../components/ReportsHeader.tsx';
import { useDocumentTitle } from '../components/useDocumentTitle.ts';

interface RetentionSection {
  activeOrgs: number;
  cancelledOrgsLifetime: number;
  cancelledOrgsInPeriod: number;
  avgActiveMonths: number | null;
  avgCancelledMonths: number | null;
  cancellationsByBucket: Array<{ bucket: string; count: number }>;
}

interface ResponseTimeSection {
  firstResponseSampleSize: number;
  firstResponseAvgMinutes: number | null;
  firstResponseP50Minutes: number | null;
  firstResponseP95Minutes: number | null;
  subsequentResponseSampleSize: number;
  subsequentResponseAvgMinutes: number | null;
  subsequentResponseP50Minutes: number | null;
  subsequentResponseP95Minutes: number | null;
}

interface ProjectDeliverySection {
  completedCount: number;
  onTimeCount: number;
  lateCount: number;
  onTimePct: number | null;
  avgDaysDiff: number | null;
  recent: Array<{
    id: string;
    name: string;
    organizationName: string;
    dueDate: string | null;
    completedAt: string | null;
    daysDiff: number | null;
  }>;
}

interface CsatSection {
  surveysSent: number;
  responses: number;
  responseRatePct: number | null;
  averageScore: number | null;
  distribution: Record<number, number>;
  recentComments: Array<{
    score: number;
    comment: string;
    submittedAt: string;
    ticketDisplayNumber: number;
    organizationName: string;
  }>;
}

interface BlockHoursRow {
  organizationId: string;
  organizationName: string;
  grantedHours: number;
  rolloverHours: number;
  consumedHours: number;
  periodCount: number;
}

// One churned block-hour client, as returned by /api/reports/health/cancellations.
interface CancelledClientRow {
  organizationId: string;
  organizationName: string;
  startedOn: string | null;
  noticeGivenOn: string | null;
  endedOn: string;
  billThrough: string;
  monthlyHours: number;
  subscriptionStatus: string;
  tenureMonths: number | null;
}

interface CancellationsState {
  loading: boolean;
  error: string | null;
  clients: CancelledClientRow[] | null;
  totalMonthlyHours: number;
}

interface HealthResponse {
  period: { granularity: 'month' | 'quarter'; start: string; end: string; label: string };
  retention: RetentionSection;
  responseTime: ResponseTimeSection;
  projectDelivery: ProjectDeliverySection;
  csat: CsatSection;
  blockHoursPerOrg: BlockHoursRow[];
  blockHoursOnBooks: Array<{ bucket: string; grantedHours: number }>;
  openTicketsByAge: Array<{ bucket: string; count: number }>;
}

function formatMinutes(min: number | null): string {
  if (min === null) return '—';
  if (min < 60) return `${min.toFixed(0)}m`;
  if (min < 60 * 24) return `${(min / 60).toFixed(1)}h`;
  return `${(min / (60 * 24)).toFixed(1)}d`;
}

export function ReportsHealthPage(): React.ReactElement {
  useDocumentTitle('Service health');
  const { agent } = useAuth();
  const { period } = usePeriod();
  const [data, setData] = useState<HealthResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  // null = collapsed. Set (even while loading) = the drill-down panel is open.
  const [cancellations, setCancellations] = useState<CancellationsState | null>(null);

  const periodQuery = useMemo(
    () =>
      new URLSearchParams({
        granularity: period.granularity,
        periodStart: period.periodStart,
      }).toString(),
    [period.granularity, period.periodStart],
  );

  useEffect(() => {
    let cancelled = false;
    setError(null);
    // A different period means a different client list — collapse rather than
    // leave the previous month's names on screen under the new heading.
    setCancellations(null);
    apiFetch<HealthResponse>(`/api/reports/health?${periodQuery}`)
      .then((r) => {
        if (!cancelled) setData(r);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : 'Failed to load.');
      });
    return () => {
      cancelled = true;
    };
  }, [periodQuery]);

  function loadCancellations(): void {
    setCancellations({ loading: true, error: null, clients: null, totalMonthlyHours: 0 });
    apiFetch<{ clients: CancelledClientRow[]; totalMonthlyHours: number }>(
      `/api/reports/health/cancellations?${periodQuery}`,
    )
      .then((r) => {
        setCancellations({
          loading: false,
          error: null,
          clients: r.clients,
          totalMonthlyHours: r.totalMonthlyHours,
        });
      })
      .catch((err) => {
        setCancellations({
          loading: false,
          error: err instanceof ApiError ? err.message : 'Failed to load the client list.',
          clients: null,
          totalMonthlyHours: 0,
        });
      });
  }

  function toggleCancellations(): void {
    if (cancellations) {
      setCancellations(null);
      return;
    }
    loadCancellations();
  }

  if (!agent) return <p>Loading…</p>;

  return (
    <div className="space-y-6">
      <ReportsHeader
        title="Reports"
        description="Service-wide health: retention, response time, project delivery, CSAT, block hours, ticket age. Period selector applies to every section that's time-bounded."
      />

      {error && (
        <Card className="border-[var(--color-danger)]/30 bg-red-50/40">
          <p className="text-sm text-[var(--color-danger)]">{error}</p>
        </Card>
      )}

      {!data ? (
        <p className="text-[var(--color-muted)]">Loading…</p>
      ) : (
        <>
          {/* RETENTION + CANCELLATION HEADLINE */}
          <section>
            <h2 className="mb-2 text-base font-semibold text-[var(--color-header)]">Block-hour client retention</h2>
            <p className="mb-3 text-xs text-[var(--color-muted)]">
              Block-hour clients only (orgs with a block-hour subscription); project-only clients are
              excluded. Cancellations are counted by each client's end date (the month their
              relationship with us ended). Active = current block-hour clients that haven't ended.
            </p>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Stat label="Active orgs" value={String(data.retention.activeOrgs)} />
              <Stat
                label={`Cancellations · ${data.period.label}`}
                value={String(data.retention.cancelledOrgsInPeriod)}
                danger={data.retention.cancelledOrgsInPeriod > 0}
                sub={
                  data.retention.cancelledOrgsInPeriod === 0
                    ? `Nothing to cancel in ${data.period.label}`
                    : undefined
                }
                hint={
                  data.retention.cancelledOrgsInPeriod > 0 ? 'Show which clients →' : undefined
                }
                onClick={
                  data.retention.cancelledOrgsInPeriod > 0 ? toggleCancellations : undefined
                }
                expanded={cancellations !== null}
              />
              <Stat
                label="Cancelled orgs · lifetime"
                value={String(data.retention.cancelledOrgsLifetime)}
              />
              <Stat
                label="Avg duration · active"
                value={
                  data.retention.avgActiveMonths !== null
                    ? `${data.retention.avgActiveMonths.toFixed(1)} mo`
                    : '—'
                }
                sub={
                  data.retention.avgCancelledMonths !== null
                    ? `${data.retention.avgCancelledMonths.toFixed(1)} mo at cancellation`
                    : undefined
                }
              />
            </div>

            {cancellations && (
              <CancellationsPanel
                state={cancellations}
                periodLabel={data.period.label}
                onRetry={loadCancellations}
              />
            )}

            <Card className="mt-4">
              <h3 className="text-sm font-semibold text-[var(--color-header)]">
                Cancellations per {data.period.granularity}
              </h3>
              {data.retention.cancellationsByBucket.length === 0 ? (
                <p className="mt-3 text-sm text-[var(--color-muted)]">None on record.</p>
              ) : (
                <BucketBars buckets={data.retention.cancellationsByBucket} />
              )}
            </Card>
          </section>

          {/* RESPONSE TIME */}
          <section>
            <h2 className="mb-2 text-base font-semibold text-[var(--color-header)]">Response time</h2>
            <p className="mb-3 text-xs text-[var(--color-muted)]">
              Window: {data.period.label}. First response = ticket created → first agent public
              reply. Subsequent = each later client reply → next agent public reply.
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
              <Card>
                <h3 className="text-sm font-semibold text-[var(--color-header)]">First response</h3>
                <p className="text-xs text-[var(--color-muted)]">
                  {data.responseTime.firstResponseSampleSize} samples
                </p>
                <div className="mt-3 grid grid-cols-3 gap-2 text-sm">
                  <Mini label="Average" value={formatMinutes(data.responseTime.firstResponseAvgMinutes)} />
                  <Mini label="Median (p50)" value={formatMinutes(data.responseTime.firstResponseP50Minutes)} />
                  <Mini label="p95" value={formatMinutes(data.responseTime.firstResponseP95Minutes)} />
                </div>
              </Card>
              <Card>
                <h3 className="text-sm font-semibold text-[var(--color-header)]">Subsequent responses</h3>
                <p className="text-xs text-[var(--color-muted)]">
                  {data.responseTime.subsequentResponseSampleSize} samples
                </p>
                <div className="mt-3 grid grid-cols-3 gap-2 text-sm">
                  <Mini label="Average" value={formatMinutes(data.responseTime.subsequentResponseAvgMinutes)} />
                  <Mini label="Median (p50)" value={formatMinutes(data.responseTime.subsequentResponseP50Minutes)} />
                  <Mini label="p95" value={formatMinutes(data.responseTime.subsequentResponseP95Minutes)} />
                </div>
              </Card>
            </div>
          </section>

          {/* PROJECT DELIVERY */}
          <section>
            <h2 className="mb-2 text-base font-semibold text-[var(--color-header)]">Project delivery</h2>
            <p className="mb-3 text-xs text-[var(--color-muted)]">
              Projects completed within the selected period, compared against their <code>due_date</code>.
            </p>
            <div className="grid gap-4 sm:grid-cols-4">
              <Stat label="Completed" value={String(data.projectDelivery.completedCount)} />
              <Stat label="On-time" value={String(data.projectDelivery.onTimeCount)} />
              <Stat
                label="Late"
                value={String(data.projectDelivery.lateCount)}
                danger={data.projectDelivery.lateCount > 0}
              />
              <Stat
                label="On-time %"
                value={
                  data.projectDelivery.onTimePct !== null
                    ? `${data.projectDelivery.onTimePct.toFixed(0)}%`
                    : '—'
                }
              />
            </div>
            {data.projectDelivery.avgDaysDiff !== null && (
              <p className="mt-3 text-xs text-[var(--color-muted)]">
                Avg days late (negative = early):{' '}
                <strong>{data.projectDelivery.avgDaysDiff.toFixed(1)} days</strong>
              </p>
            )}
            {data.projectDelivery.recent.length > 0 && (
              <div className="mt-4 overflow-hidden rounded-[var(--radius-card)] border border-[var(--color-border)] bg-white">
                <table className="w-full text-sm">
                  <thead className="bg-[var(--color-surface)] text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
                    <tr>
                      <th className="px-3 py-2">Project</th>
                      <th className="px-3 py-2">Org</th>
                      <th className="px-3 py-2">Due</th>
                      <th className="px-3 py-2">Completed</th>
                      <th className="px-3 py-2 text-right">Δ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.projectDelivery.recent.map((p) => (
                      <tr key={p.id} className="border-t border-[var(--color-border)]">
                        <td className="px-3 py-2 font-medium">{p.name}</td>
                        <td className="px-3 py-2">{p.organizationName}</td>
                        <td className="px-3 py-2 text-xs text-[var(--color-muted)]">{p.dueDate ?? '—'}</td>
                        <td className="px-3 py-2 text-xs text-[var(--color-muted)]">
                          {p.completedAt ? new Date(p.completedAt).toLocaleDateString() : '—'}
                        </td>
                        <td
                          className={
                            'px-3 py-2 text-right font-mono ' +
                            ((p.daysDiff ?? 0) > 0 ? 'text-[var(--color-danger)]' : 'text-emerald-700')
                          }
                        >
                          {p.daysDiff !== null
                            ? `${p.daysDiff > 0 ? '+' : ''}${p.daysDiff.toFixed(0)}d`
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* CSAT */}
          <section>
            <h2 className="mb-2 text-base font-semibold text-[var(--color-header)]">Client satisfaction</h2>
            <p className="mb-3 text-xs text-[var(--color-muted)]">
              1–5 star surveys sent on resolution. Window: {data.period.label}.
            </p>
            <div className="grid gap-4 sm:grid-cols-4">
              <Stat label="Surveys sent" value={String(data.csat.surveysSent)} />
              <Stat label="Responses" value={String(data.csat.responses)} />
              <Stat
                label="Response rate"
                value={
                  data.csat.responseRatePct !== null
                    ? `${data.csat.responseRatePct.toFixed(0)}%`
                    : '—'
                }
              />
              <Stat
                label="Avg score"
                value={
                  data.csat.averageScore !== null ? `${data.csat.averageScore.toFixed(2)} ★` : '—'
                }
              />
            </div>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <Card>
                <h3 className="text-sm font-semibold text-[var(--color-header)]">Score distribution</h3>
                <ul className="mt-3 space-y-1 text-sm">
                  {[5, 4, 3, 2, 1].map((s) => {
                    const count = data.csat.distribution[s] ?? 0;
                    const max = Math.max(...Object.values(data.csat.distribution), 1);
                    return (
                      <li key={s} className="grid grid-cols-12 items-center gap-2">
                        <span className="col-span-2 font-mono">{s} ★</span>
                        <div className="col-span-8 h-3 overflow-hidden rounded-full bg-[var(--color-border)]">
                          <div
                            className={
                              'h-full ' +
                              (s >= 4
                                ? 'bg-emerald-500'
                                : s === 3
                                  ? 'bg-amber-400'
                                  : 'bg-[var(--color-danger)]')
                            }
                            style={{ width: `${(count / max) * 100}%` }}
                          />
                        </div>
                        <span className="col-span-2 text-right font-mono">{count}</span>
                      </li>
                    );
                  })}
                </ul>
              </Card>
              <Card>
                <h3 className="text-sm font-semibold text-[var(--color-header)]">Recent comments</h3>
                {data.csat.recentComments.length === 0 ? (
                  <p className="mt-3 text-sm text-[var(--color-muted)]">No comments in this window.</p>
                ) : (
                  <ul className="mt-3 space-y-3 text-sm">
                    {data.csat.recentComments.map((c, i) => (
                      <li key={i} className="border-t border-[var(--color-border)] pt-2 first:border-t-0 first:pt-0">
                        <div className="flex items-baseline justify-between">
                          <span className="font-mono">{c.score} ★</span>
                          <span className="text-xs text-[var(--color-muted)]">
                            T-{c.ticketDisplayNumber} · {c.organizationName} ·{' '}
                            {new Date(c.submittedAt).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="mt-1 whitespace-pre-wrap text-sm text-[var(--color-body)]">{c.comment}</p>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
            </div>
          </section>

          {/* BLOCK HOURS ON THE BOOKS BY MONTH (trailing series) */}
          <section>
            <h2 className="mb-2 text-base font-semibold text-[var(--color-header)]">
              Block hours on the books by {data.period.granularity}
            </h2>
            <p className="mb-3 text-xs text-[var(--color-muted)]">
              Total hours granted each {data.period.granularity} across all clients (base + ad-hoc +
              rollover-in) — the capacity on the books. Trailing {data.period.granularity === 'quarter' ? '12 quarters' : '12 months'}.
            </p>
            <Card>
              {data.blockHoursOnBooks.length === 0 ? (
                <p className="text-sm text-[var(--color-muted)]">No block-hour periods on record.</p>
              ) : (
                <HoursBucketBars buckets={data.blockHoursOnBooks} />
              )}
            </Card>
          </section>

          {/* BLOCK HOURS (period-bound) */}
          <section>
            <h2 className="mb-2 text-base font-semibold text-[var(--color-header)]">
              Block hours by org · {data.period.label}
            </h2>
            <p className="mb-3 text-xs text-[var(--color-muted)]">
              {data.period.granularity === 'quarter'
                ? 'Granted and used hours summed across the months in the selected quarter. Carried-over rollover is counted once, at the start of the quarter.'
                : 'Granted (base + ad-hoc + rollover in) vs. used for the selected month.'}
            </p>
            <Card>
              {data.blockHoursPerOrg.length === 0 ? (
                <p className="text-sm text-[var(--color-muted)]">
                  No block-hour periods in {data.period.label}.
                </p>
              ) : (
                <div className="overflow-hidden rounded-[var(--radius-button)] border border-[var(--color-border)]">
                  <table className="w-full text-sm">
                    <thead className="bg-[var(--color-surface)] text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
                      <tr>
                        <th className="px-3 py-2">Org</th>
                        <th className="px-3 py-2 text-right">Granted</th>
                        <th className="px-3 py-2 text-right">Rollover</th>
                        <th className="px-3 py-2 text-right">Used</th>
                        <th className="px-3 py-2 text-right">Remaining</th>
                        <th className="px-3 py-2">Progress</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.blockHoursPerOrg.map((r) => {
                        const remaining = Math.max(0, r.grantedHours - r.consumedHours);
                        const pct =
                          r.grantedHours > 0
                            ? Math.min(100, (r.consumedHours / r.grantedHours) * 100)
                            : 0;
                        const overShallow = pct >= 80;
                        return (
                          <tr key={r.organizationId} className="border-t border-[var(--color-border)]">
                            <td className="px-3 py-2">{r.organizationName}</td>
                            <td className="px-3 py-2 text-right font-mono">{r.grantedHours.toFixed(2)}h</td>
                            <td
                              className={
                                'px-3 py-2 text-right font-mono ' +
                                (r.rolloverHours > 0 ? '' : 'text-[var(--color-muted)]')
                              }
                            >
                              {r.rolloverHours.toFixed(2)}h
                            </td>
                            <td className="px-3 py-2 text-right font-mono">{r.consumedHours.toFixed(2)}h</td>
                            <td
                              className={
                                'px-3 py-2 text-right font-mono ' +
                                (overShallow ? 'text-[var(--color-danger)]' : '')
                              }
                            >
                              {remaining.toFixed(2)}h
                            </td>
                            <td className="px-3 py-2">
                              <div className="h-1.5 w-32 overflow-hidden rounded-full bg-[var(--color-border)]">
                                <div
                                  className={
                                    'h-full ' +
                                    (overShallow ? 'bg-[var(--color-danger)]' : 'bg-[var(--color-t3-blue)]')
                                  }
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </section>

          {/* OPEN TICKETS BY AGE (live, ignores period) */}
          <section>
            <h2 className="mb-2 text-base font-semibold text-[var(--color-header)]">Open tickets by age (live)</h2>
            <Card>
              <div className="grid grid-cols-4 gap-2 text-center">
                {(['<1d', '1-7d', '7-30d', '>30d'] as const).map((bucket) => {
                  const row = data.openTicketsByAge.find((r) => r.bucket === bucket);
                  const n = row?.count ?? 0;
                  return (
                    <div
                      key={bucket}
                      className={
                        'rounded-[var(--radius-button)] border p-3 ' +
                        (bucket === '>30d' && n > 0
                          ? 'border-[var(--color-danger)]/30 bg-red-50/40'
                          : 'border-[var(--color-border)] bg-white')
                      }
                    >
                      <div className="text-xs uppercase tracking-wide text-[var(--color-muted)]">{bucket}</div>
                      <div className="mt-1 text-2xl font-bold text-[var(--color-header)]">{n}</div>
                    </div>
                  );
                })}
              </div>
            </Card>
          </section>
        </>
      )}
    </div>
  );
}

// A stat card. Pass `onClick` to make it a drill-down trigger — it then renders
// as a real <button> (keyboard-focusable, aria-expanded) with a chevron. Without
// `onClick` it stays an inert card, so a zero count simply isn't clickable.
function Stat({
  label,
  value,
  sub,
  danger,
  hint,
  onClick,
  expanded,
}: {
  label: string;
  value: string;
  sub?: string;
  danger?: boolean;
  hint?: string;
  onClick?: () => void;
  expanded?: boolean;
}): React.ReactElement {
  const body = (
    <>
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs uppercase tracking-wide text-[var(--color-muted)]">{label}</div>
        {onClick && (
          <ChevronDown
            className={
              'h-4 w-4 shrink-0 text-[var(--color-muted)] transition-transform duration-200 ' +
              (expanded ? 'rotate-180' : '')
            }
          />
        )}
      </div>
      <div
        className={
          'mt-1 text-2xl font-bold ' +
          (danger ? 'text-[var(--color-danger)]' : 'text-[var(--color-header)]')
        }
      >
        {value}
      </div>
      {sub && <div className="mt-0.5 text-xs text-[var(--color-muted)]">{sub}</div>}
      {hint && (
        <div className="mt-1.5 text-xs font-medium text-[var(--color-t3-blue)]">{hint}</div>
      )}
    </>
  );

  const borderClass = danger ? 'border-[var(--color-danger)]/40' : '';

  if (!onClick) return <Card className={borderClass}>{body}</Card>;

  return (
    <Card className={borderClass + ' cursor-pointer text-left transition hover:shadow-md'}>
      <button
        type="button"
        onClick={onClick}
        aria-expanded={expanded === true}
        className="block w-full rounded-[var(--radius-button)] text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-t3-blue)] focus-visible:ring-offset-2"
      >
        {body}
      </button>
    </Card>
  );
}

// Drill-down behind the cancellations card: the actual clients whose service
// ended in the selected period — i.e. the ones to stop billing in Stripe.
function CancellationsPanel({
  state,
  periodLabel,
  onRetry,
}: {
  state: CancellationsState;
  periodLabel: string;
  onRetry: () => void;
}): React.ReactElement {
  return (
    <div className="mt-4 overflow-hidden rounded-[var(--radius-card)] border border-[var(--color-border)] bg-white shadow-sm">
      <div className="px-6 pb-4 pt-5">
        <h3 className="text-sm font-semibold text-[var(--color-header)]">
          Clients ending in {periodLabel} — cancel these in Stripe
        </h3>
        <p className="mt-1 max-w-[62ch] text-xs text-[var(--color-muted)]">
          Ordered by end date. <strong>Bill through</strong> is the last day services apply, so the
          billing subscription must not renew after it. <strong>Our subscription</strong> is the
          block-hour record in this app — anything not <em>Cancelled</em> still needs closing out
          here too.
        </p>
      </div>

      {state.loading && (
        <p className="px-6 pb-6 text-sm text-[var(--color-muted)]">Loading clients…</p>
      )}

      {state.error && (
        <p className="px-6 pb-6 text-sm text-[var(--color-danger)]">
          {state.error}{' '}
          <button
            type="button"
            onClick={onRetry}
            className="font-semibold text-[var(--color-t3-blue)] underline"
          >
            Try again
          </button>
        </p>
      )}

      {state.clients && state.clients.length === 0 && (
        <p className="px-6 pb-6 text-sm text-[var(--color-muted)]">
          No block-hour clients ended in {periodLabel}.
        </p>
      )}

      {state.clients && state.clients.length > 0 && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[var(--color-surface)] text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
                <tr>
                  <th className="px-6 py-2.5">Organization</th>
                  <th className="px-6 py-2.5">Notice given</th>
                  <th className="px-6 py-2.5">Bill through</th>
                  <th className="px-6 py-2.5">Ends on</th>
                  <th className="px-6 py-2.5 text-right">Monthly hours</th>
                  <th className="px-6 py-2.5 text-right">Tenure</th>
                  <th className="px-6 py-2.5">Our subscription</th>
                </tr>
              </thead>
              <tbody>
                {state.clients.map((c) => (
                  <tr key={c.organizationId} className="border-t border-[var(--color-border)]">
                    <td className="px-6 py-3">
                      <Link
                        to={`/organizations/${c.organizationId}`}
                        className="font-semibold text-[var(--color-t3-blue)] hover:underline"
                      >
                        {c.organizationName}
                      </Link>
                    </td>
                    <td className="px-6 py-3 font-mono text-xs text-[var(--color-muted)]">
                      {c.noticeGivenOn ?? '—'}
                    </td>
                    <td className="px-6 py-3 font-mono text-xs">{c.billThrough}</td>
                    <td className="px-6 py-3 font-mono text-xs text-[var(--color-muted)]">
                      {c.endedOn}
                    </td>
                    <td className="px-6 py-3 text-right font-mono">{c.monthlyHours}h</td>
                    <td className="px-6 py-3 text-right font-mono text-xs text-[var(--color-muted)]">
                      {c.tenureMonths !== null ? `${c.tenureMonths.toFixed(1)} mo` : '—'}
                    </td>
                    <td className="px-6 py-3">
                      <SubscriptionBadge status={c.subscriptionStatus} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="border-t border-[var(--color-border)] bg-[var(--color-surface)] px-6 py-3 text-xs text-[var(--color-muted)]">
            {state.clients.length} {state.clients.length === 1 ? 'client' : 'clients'} ·{' '}
            {state.totalMonthlyHours} monthly hours coming off the books
          </div>
        </>
      )}
    </div>
  );
}

// Our internal block-hour subscription state. 'active' / 'cancellation_pending'
// mean the record is still open in this app despite the client having ended.
function SubscriptionBadge({ status }: { status: string }): React.ReactElement {
  const style =
    status === 'cancelled'
      ? 'border-emerald-800/25 bg-emerald-50 text-emerald-900'
      : status === 'cancellation_pending'
        ? 'border-amber-500/30 bg-amber-50 text-amber-800'
        : 'border-[var(--color-danger)]/30 bg-red-50 text-[var(--color-danger)]';
  const label =
    status === 'cancelled'
      ? 'Cancelled'
      : status === 'cancellation_pending'
        ? 'Cancellation pending'
        : status === 'active'
          ? 'Active'
          : status;
  return (
    <span
      className={
        'inline-block rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ' + style
      }
    >
      {label}
    </span>
  );
}

function Mini({ label, value }: { label: string; value: string }): React.ReactElement {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-[var(--color-muted)]">{label}</div>
      <div className="font-mono text-sm">{value}</div>
    </div>
  );
}

function BucketBars({
  buckets,
}: {
  buckets: Array<{ bucket: string; count: number }>;
}): React.ReactElement {
  const max = Math.max(...buckets.map((b) => b.count), 1);
  return (
    <ul className="mt-3 space-y-1.5 text-sm">
      {buckets.map((b) => (
        <li key={b.bucket} className="grid grid-cols-12 items-center gap-2">
          <span className="col-span-3 font-mono text-xs text-[var(--color-muted)]">{b.bucket}</span>
          <div className="col-span-7 h-3 overflow-hidden rounded-full bg-[var(--color-border)]">
            <div
              className="h-full bg-[var(--color-t3-purple)]"
              style={{ width: `${(b.count / max) * 100}%` }}
            />
          </div>
          <span className="col-span-2 text-right font-mono">{b.count}</span>
        </li>
      ))}
    </ul>
  );
}

function HoursBucketBars({
  buckets,
}: {
  buckets: Array<{ bucket: string; grantedHours: number }>;
}): React.ReactElement {
  const max = Math.max(...buckets.map((b) => b.grantedHours), 1);
  return (
    <ul className="mt-1 space-y-1.5 text-sm">
      {buckets.map((b) => (
        <li key={b.bucket} className="grid grid-cols-12 items-center gap-2">
          <span className="col-span-3 font-mono text-xs text-[var(--color-muted)]">{b.bucket}</span>
          <div className="col-span-7 h-3 overflow-hidden rounded-full bg-[var(--color-border)]">
            <div
              className="h-full bg-[var(--color-t3-blue)]"
              style={{ width: `${(b.grantedHours / max) * 100}%` }}
            />
          </div>
          <span className="col-span-2 text-right font-mono">{b.grantedHours.toFixed(1)}h</span>
        </li>
      ))}
    </ul>
  );
}
